import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

interface BreadcrumbContextValue {
  leaf: string | null;
  setLeaf: (label: string | null) => void;
}

const BreadcrumbContext = createContext<BreadcrumbContextValue | null>(null);

export function BreadcrumbProvider({ children }: { children: ReactNode }) {
  // `setLeaf` from useState is stable; only `leaf` drives re-renders.
  const [leaf, setLeaf] = useState<string | null>(null);
  const value = useMemo(() => ({ leaf, setLeaf }), [leaf]);
  return (
    <BreadcrumbContext.Provider value={value}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

/** Read the current dynamic breadcrumb leaf (null when none is registered). */
export function useBreadcrumbTrailLeaf(): string | null {
  return useContext(BreadcrumbContext)?.leaf ?? null;
}

/**
 * Register a dynamic final breadcrumb crumb (e.g. a record name on a detail
 * page). Pass the label as it becomes available; it clears automatically when
 * the component unmounts or the label goes empty. No-op outside a provider.
 */
export function useBreadcrumbLeaf(label: string | null | undefined): void {
  const setLeaf = useContext(BreadcrumbContext)?.setLeaf;
  useEffect(() => {
    if (!setLeaf) return;
    const next = label && label.trim() ? label : null;
    setLeaf(next);
    return () => setLeaf(null);
  }, [setLeaf, label]);
}
